import { useState } from "react";
import type { UpdateClaimAccessRequestBlockedModulesEnum } from "@/api/generated/client/models";
import { getNextBlockedModules } from "@/lib/claim-assignee-module-utils";

interface SubmitInviteHandlers {
    assignUser: (
        userId: string,
        level: "ro" | "rw",
        blockedModules?: UpdateClaimAccessRequestBlockedModulesEnum[],
    ) => Promise<void>;
    inviteByEmail: (
        email: string,
        level: "ro" | "rw",
        blockedModules?: UpdateClaimAccessRequestBlockedModulesEnum[],
    ) => Promise<void>;
    onSuccess?: () => void;
}

export function useClaimAssigneeInviteState() {
    const [selectedAssignableUserId, setSelectedAssignableUserId] = useState<string>("");
    const [inviteEmail, setInviteEmail] = useState("");
    const [shareLevel, setShareLevel] = useState<"ro" | "rw">("rw");
    const [hiddenModules, setHiddenModules] = useState<UpdateClaimAccessRequestBlockedModulesEnum[]>([]);

    const reset = () => {
        setSelectedAssignableUserId("");
        setInviteEmail("");
        setShareLevel("rw");
        setHiddenModules([]);
    };

    const toggleHiddenModule = (
        module: UpdateClaimAccessRequestBlockedModulesEnum,
        checked: boolean,
    ) => {
        setHiddenModules((current) => getNextBlockedModules(current, module, checked));
    };

    const submitInvite = async ({ assignUser, inviteByEmail, onSuccess }: SubmitInviteHandlers): Promise<boolean> => {
        const email = inviteEmail.trim();
        const hasSelectedTeammate = selectedAssignableUserId.length > 0;
        const hasEmail = email.length > 0;

        if ((hasSelectedTeammate && hasEmail) || (!hasSelectedTeammate && !hasEmail)) {
            return false;
        }

        try {
            if (hasSelectedTeammate) {
                await assignUser(selectedAssignableUserId, shareLevel, hiddenModules);
            } else {
                await inviteByEmail(email, shareLevel, hiddenModules);
            }

            reset();
            onSuccess?.();
            return true;
        } catch {
            // Error toast is handled by useClaimAssignees.
            return false;
        }
    };

    return {
        selectedAssignableUserId,
        setSelectedAssignableUserId,
        inviteEmail,
        setInviteEmail,
        shareLevel,
        setShareLevel,
        hiddenModules,
        toggleHiddenModule,
        submitInvite,
        reset,
    };
}
